import { useState } from 'react'
import { Waypoint } from 'react-waypoint'

import { Layout, Text } from '@loadsmart/miranda-react'

import { type DistanceUnit } from '../domain/DistanceUnit'
import { type Lane } from '../domain/Lane'
import { AwardedLaneItem } from './AwardedLaneItem'

type AwardedProposalConfirmationSectionProps = {
  rfpId: string
  lanes: Array<Lane>
  title: string
  distanceUnit: DistanceUnit
}

function getLaneKey(lane: Lane) {
  /**
   * The "model" that we expect in the UI after using MultiMode
   * is to have the same lane, "duplicated" N times, one for each preference/proposal.
   * So, we need to differentiate them by the proposal.id to avoid react key warnings.
   */
  return `${lane.id}-${lane.laneProposal?.id}`
}

const BATCH_INCREMENT = 50

export function AwardedProposalConfirmationSection({
  rfpId,
  lanes,
  title,
  distanceUnit,
}: AwardedProposalConfirmationSectionProps) {
  const [visibleLanesCount, setVisibleLanesCount] = useState(BATCH_INCREMENT)

  return (
    <Layout.Stack>
      <Layout.Box
        padding="spacing-4"
        borderRadius="border-radius-s"
        borderWidth="border-thin"
        borderColor="color-neutral-40"
        backgroundColor="color-neutral-0"
      >
        <Layout.Group align="center" justify="flex-start">
          <Text variant="heading-sm-bold">{title}</Text>
          <Text variant="body-md">Bids:</Text>
          <Text variant="body-md-bold">{lanes.length}</Text>
        </Layout.Group>
      </Layout.Box>
      {lanes.slice(0, visibleLanesCount).map((lane, index) => (
        <AwardedLaneItem
          rfpId={rfpId}
          position={index}
          awardedLane={lane}
          isAwardFinished
          key={getLaneKey(lane)}
          distanceUnit={distanceUnit}
        />
      ))}
      <Waypoint
        onEnter={() => {
          /**
           * @TODO: Review this solution
           *
           * "Quick and naive" solution to avoid rendering lanes all lanes at once.
           * Having many lanes was causing the page to freeze in some cases.
           * So, we are rendering only the first 50 lanes and then incrementing 50 more each time,
           * levering React capacity to not render/commit again, based in the key, the same items.
           *
           * This will be refactored in future work to implement pagination.
           */
          if (visibleLanesCount < lanes.length) {
            setVisibleLanesCount(visibleLanesCount + BATCH_INCREMENT)
          }
        }}
      />
    </Layout.Stack>
  )
}
